import { useTranslation } from "react-i18next"

import type { DeploymentSwitch } from "@/api/admin-api"

import { AlertCircle, RefreshCw, SlidersHorizontal } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useDeploymentSwitchesQuery } from "@/features/admin-dashboard/hooks/admin-queries"
import { cn } from "@/lib/utils"

import { DEPLOYMENT_SWITCH_SUBSYSTEMS } from "./deployment-switches-constants"

// Read-only effective deployment switches (spec R6.5). Answers "which backends
// is this process actually running?" — and only that. The route reports what
// the process resolved at BOOT (R6.2); this page never sees the customer env
// file, so it cannot show a disagreement with it. What it can show is the age
// of the boot: a switch edited on the host is live only after a restart, so a
// start time older than the edit means the edit is not in effect yet. The
// operator compares the two; the card supplies one of them.

function formatUptime(seconds: number) {
  // The route sends seconds and computes them server-side on purpose: a viewer
  // with a skewed clock would otherwise read the wrong age, and the age is the
  // entire drift signal this card exists to give.
  if (!Number.isFinite(seconds) || seconds < 0) return "unknown"
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

function formatStartTime(value: string | null) {
  // The route sends null when it has no recorded start time, and pairs it with
  // uptime_seconds: 0 — which formatUptime would otherwise render as a
  // confident "0m", i.e. "this process started just now". Say unknown instead.
  if (!value) return null
  const parsed = Date.parse(value)
  // The route reports an ISO-8601 timestamp. Anything Date cannot parse is
  // shown verbatim: rendering "Invalid Date" would read as a broken panel and
  // would hide the one value an operator needs in order to judge drift.
  return Number.isNaN(parsed) ? value : new Date(parsed).toLocaleString()
}

function presentCompanionSettings(present: Record<string, boolean>) {
  // R6.4: companion endpoints and credentials are reported by presence only, so
  // the card can name them but must never render a value.
  return Object.entries(present)
    .filter(([, isPresent]) => isPresent)
    .map(([name]) => name)
}

function SwitchRow({ item }: { item: DeploymentSwitch }) {
  const { t } = useTranslation()
  const subsystem = DEPLOYMENT_SWITCH_SUBSYSTEMS[item.name]
  const fromEnvironment = item.source === "environment"
  const description = subsystem
    ? t(`adminDashboard.deploymentSwitches.subsystem.${item.name}`, { defaultValue: subsystem })
    : ""
  const sourceLabel = fromEnvironment
    ? t("adminDashboard.deploymentSwitches.sourceEnvironment", { defaultValue: "environment" })
    : t("adminDashboard.deploymentSwitches.sourceDefault", { defaultValue: "code default" })
  const codeDefaultLabel = t("adminDashboard.deploymentSwitches.codeDefault", {
    defaultValue: "Code default",
  })

  return (
    <li className="flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
      <div className="min-w-0">
        <div className="font-mono text-sm font-medium">{item.name}</div>
        {description && <div className="text-muted-foreground text-xs">{description}</div>}
        {item.environment_drift && (
          // NOT the edit-without-restart signal, whatever this said before. A
          // container's environment is immutable after create, so this can only
          // be true when something mutated the process environment between
          // import and boot capture. The drift signal an operator can act on is
          // the process start time above.
          <div className="text-warning flex items-center gap-1 text-xs">
            <AlertCircle className="size-3" />
            {t("adminDashboard.deploymentSwitches.environmentDrift", {
              defaultValue: "The environment no longer matches this running value",
            })}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs">{item.value}</span>
        <span
          className={cn("text-xs", fromEnvironment ? "text-foreground" : "text-muted-foreground")}
          title={`${codeDefaultLabel}: ${item.default}`}
        >
          {sourceLabel}
        </span>
      </div>
    </li>
  )
}

export function DeploymentSwitchesCard() {
  const { t } = useTranslation()
  const { data, isLoading, isFetching, isError, refetch } = useDeploymentSwitchesQuery()
  const switches = data?.switches ?? []
  const companions = data ? presentCompanionSettings(data.companion_settings_present) : []
  // #3071. Empty on every coherent deployment, so the row below is absent
  // rather than reassuring: a drift panel that prints "no mismatch" on six
  // splits where a mismatch is impossible teaches the reader to skip it.
  // `?? []` covers a backend older than the field as "nothing to report".
  const libraryStorageMismatch = data?.library_storage_mismatch ?? []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <SlidersHorizontal className="size-5" />
            {t("adminDashboard.deploymentSwitches.title", {
              defaultValue: "Deployment switches",
            })}
            {data?.on_prem && (
              <Badge variant="secondary">
                {t("adminDashboard.deploymentSwitches.onPrem", { defaultValue: "on-prem" })}
              </Badge>
            )}
          </CardTitle>
          <Button disabled={isFetching} size="sm" variant="outline" onClick={() => void refetch()}>
            <RefreshCw className={cn("mr-2 size-4", isFetching && "animate-spin")} />
            {t("adminDashboard.deploymentSwitches.refresh", { defaultValue: "Refresh" })}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        )}
        {isError && (
          <div className="text-destructive flex items-center gap-2 py-4 text-sm">
            <AlertCircle className="size-4" />
            {t("adminDashboard.deploymentSwitches.loadError", {
              defaultValue:
                "Failed to load the effective deployment switches — they are available once backend startup has completed.",
            })}
          </div>
        )}
        {data && (
          <div className="space-y-3">
            {libraryStorageMismatch.length > 0 && (
              <div className="border-warning/40 bg-warning/10 space-y-1 rounded-md border p-3 text-xs">
                <div className="text-warning flex items-center gap-1 font-medium">
                  <AlertCircle className="size-3.5" />
                  {t("adminDashboard.deploymentSwitches.libraryStorageMismatchTitle", {
                    defaultValue: "Library files are missing from local storage",
                  })}
                </div>
                <div className="text-muted-foreground">
                  {t("adminDashboard.deploymentSwitches.libraryStorageMismatchBody", {
                    defaultValue:
                      "SEARCH_BACKEND=azure with STORAGE_BACKEND=azurite: the shared Azure AI Search index returns hits for libraries whose blob container does not exist in this stack's local storage, so citations answer 404 and some searches time out. Run 'poc_deploy_customer.py sync-library-blobs' to copy them in.",
                  })}
                </div>
                <div>
                  {t("adminDashboard.deploymentSwitches.libraryStorageMismatchLibraries", {
                    defaultValue: "Affected libraries",
                  })}
                  : <span className="font-mono">{libraryStorageMismatch.join(", ")}</span>
                </div>
              </div>
            )}
            {switches.length > 0 ? (
              // A list, not a stack of divs: the panel is an enumeration, and
              // the list semantics are what a screen reader (and the test)
              // navigate it by.
              <ul>
                {switches.map((item) => (
                  <SwitchRow key={item.name} item={item} />
                ))}
              </ul>
            ) : (
              // Never a blank card: an empty list is itself an answer, and it is
              // the visible symptom if the route ever stops reporting switches.
              <p className="text-muted-foreground py-4 text-center text-sm">
                {t("adminDashboard.deploymentSwitches.empty", {
                  defaultValue: "No switches reported.",
                })}
              </p>
            )}
            <div className="text-muted-foreground space-y-1 text-xs">
              <div>
                {t("adminDashboard.deploymentSwitches.processStarted", {
                  defaultValue: "Process started",
                })}
                :{" "}
                {formatStartTime(data.process_start_time) === null
                  ? t("adminDashboard.deploymentSwitches.startUnknown", {
                      defaultValue: "unknown — this process has not recorded a start time",
                    })
                  : `${formatStartTime(data.process_start_time)} (${formatUptime(data.uptime_seconds)})`}
              </div>
              <div>
                {t("adminDashboard.deploymentSwitches.companionSettings", {
                  defaultValue: "Companion settings present",
                })}
                : {companions.length > 0 ? companions.join(", ") : "—"}
              </div>
              <div>
                {t("adminDashboard.deploymentSwitches.bootCaptureNote", {
                  defaultValue:
                    "Values are captured when the backend process starts. Editing the environment without restarting the stack does not change what is running.",
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
